'use client';

// TenantProvider (client) — TRỤC 1 entitlement phía UI.
// Cung cấp plan + branding + planAllows() cho Sidebar/page guards.
// Sidebar hiển thị item ⇔ planAllows(key) && can(key)  (can() từ usePermissions, TRỤC 2).
//
// tenantId resolve theo thứ tự:
//   1) custom claim `tenantId` (getIdTokenResult)
//   2) DEFAULT_TENANT_ID ('gardenia') trong giai đoạn chuyển tiếp
// (Khi bật subdomain routing — Phase 2 — middleware sẽ là nguồn ưu tiên, vẫn đối chiếu claim.)

import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { doc } from 'firebase/firestore';
import { getDoc, onSnapshot } from './fsx';
import { getFirebaseDb } from './firebase';
import { tenantColClient, tenantDocClient } from './tenant-db-client';
import { useAuth } from './auth';
import { logClientError } from './client-error-logger';
import { withNetworkRetry } from './network-retry';
import { traceUpdate } from './render-update-tracer';
import { queueSharedUpdate, dropSharedUpdate } from './shared-update-batcher';
import { scheduleBootCall } from './boot-call-queue';
import {
  planMatrixAllows, mergePlanMatrix, mergePlanLimits, isPlan, PLAN_LIMITS, PLANS,
  type Plan, type PlanFeatureMatrix, type PlanLimits, type PlanLimitsMap,
} from './plans';
import type { BillingStatus, TenantBillingMeta } from '@/types';

const PLAN_FEATURES_KEY = 'tenant-context:planFeatures';
const BOOT_KEY = 'tenant-context:boot';

const DEFAULT_TENANT_ID = 'gardenia';
// Default 'host_pro' khi chưa có tenant doc → không ẩn nhầm feature của Gardenia trong chuyển tiếp.
const DEFAULT_PLAN: Plan = 'host_pro';

interface TenantBranding {
  logoUrl?: string;
  brandColor?: string;
}

interface TenantContextType {
  tenantId: string;
  tenantName: string;
  plan: Plan;
  branding: TenantBranding | null;
  loading: boolean;
  /** Tenant có bật module HOLA không (tenants/{tid}.holaEnabled). Quyết định hiện nhóm menu HOLA + trang đích sau đăng nhập. */
  holaEnabled: boolean;
  /** Gói hiện tại có feature này không (TRỤC 1). KHÔNG thay can() của usePermissions (TRỤC 2). */
  planAllows: (featureKey: string) => boolean;
  /** Gói THẤP NHẤT (theo matrix ĐỘNG hiện hành, KHÔNG phải default tĩnh) có feature này — PlanGuard
   *  dùng để hiện "có ở gói X trở lên". Phải cùng nguồn `planMatrix` với `planAllows` ở trên — dùng
   *  bảng tĩnh `PLAN_FEATURES` riêng (bug thật 2026-09-23) có thể LỆCH với matrix đã bị super-admin
   *  sửa qua `/admin/plans`, ra câu tự mâu thuẫn "chưa có trong gói Miễn phí — có ở gói Miễn phí trở lên". */
  planUnlockTier: (featureKey: string) => Plan | null;
  /** Giới hạn hiệu lực của gói hiện tại = limit gói (động) override bởi tenants/{id}.limits. */
  planLimits: PlanLimits;
  /** Trạng thái thanh toán SaaS — BillingLockBanner đọc để hiện soft/full lock. */
  billing: { status: BillingStatus; lockAt: number | null } | null;
  /** Tenant bị operator tạm khoá (tenants/{id}.status === 'suspended') → server chặn 403; guard hiện overlay. */
  suspended: boolean;
  /** 'network' = đã tự thử lại `getDoc(tenants/{tid})` (đường lùi khi thiếu `bootTenant`) hết số lần
   *  cho phép mà vẫn treo/mất kết nối. `TenantLoadingGuard` hiện màn "Thử lại" thay vì đứng mãi. */
  error: 'network' | null;
  /** Đang trong 1 trong các lần tự thử lại — cùng vai trò với `bootRetry` ở `lib/auth.tsx`. */
  retry: { attempt: number; max: number } | null;
  /** Thử lại tải tenant doc — dùng khi `error === 'network'`. */
  reload: () => void;
}

const TenantContext = createContext<TenantContextType | null>(null);

export function TenantProvider({ children }: Readonly<{ children: React.ReactNode }>) {
  const { firebaseUser, user, bootTenant } = useAuth();
  const [tenantId, setTenantId] = useState<string>(DEFAULT_TENANT_ID);
  const [tenantName, setTenantName] = useState<string>('');
  const [plan, setPlan] = useState<Plan>(DEFAULT_PLAN);
  const [branding, setBranding] = useState<TenantBranding | null>(null);
  // Matrix + limit gói động (root system/planFeatures) — realtime qua onSnapshot.
  // null = chưa tải → planAllows fallback tĩnh (không ẩn nhầm).
  const [planMatrix, setPlanMatrix] = useState<PlanFeatureMatrix | null>(null);
  const [planLimitsMap, setPlanLimitsMap] = useState<PlanLimitsMap>(PLAN_LIMITS);
  const [tenantLimitOverride, setTenantLimitOverride] = useState<Partial<PlanLimits>>({});
  const [billingStatus, setBillingStatus] = useState<BillingStatus>('active');
  const [billingLockAt, setBillingLockAt] = useState<number | null>(null);
  const [suspended, setSuspended] = useState(false);
  const [holaEnabled, setHolaEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [tenantError, setTenantError] = useState<'network' | null>(null);
  const [tenantRetry, setTenantRetry] = useState<{ attempt: number; max: number } | null>(null);
  const [reloadTick, setReloadTick] = useState(0);
  const reload = useCallback(() => setReloadTick(t => t + 1), []);

  // Tenant doc: plan / branding / limit-override riêng tenant.
  //
  // ⚡ ĐƯỜNG NHANH (2026-09-05): `bootTenant` do /api/auth/sync-profile trả kèm (hoặc cache khởi động)
  //    → áp ngay, KHÔNG cần getIdTokenResult + getDoc(tenants/{tid}) nữa. Quan trọng vì TenantLoadingGuard
  //    chặn render toàn app tới khi loading=false: mỗi round-trip ở đây là thời gian user nhìn màn chờ.
  //    bootTenant lấy từ profile.tenantId (nguồn chân lý users doc; auth.tsx đã đối chiếu + force-refresh
  //    token khi claim lệch) → an toàn ngang đường cũ. Nếu ban đầu là cache CŨ (user vừa bị chuyển tenant),
  //    sync-profile trả về sẽ cập nhật bootTenant → effect chạy lại và tự sửa.
  //    ĐƯỜNG CŨ giữ nguyên làm fallback khi thiếu bootTenant (server không đọc được tenant doc).
  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!firebaseUser) { setLoading(false); return; }
      if (bootTenant) {
        // Bug thật 2026-09-14 (race condition multi-workspace): `bootTenant` có thể đến từ
        // `lib/boot-cache.ts` (localStorage, khoá theo uid — KHÔNG khoá theo tenant đang hoạt động).
        // Tài khoản workspaceSwitch/impersonate đổi qua lại giữa các tenant → cache còn giữ tenant
        // CŨ từ lần trước → tin ngay `bootTenant.id` khiến TenantLoadingGuard mở khoá render với
        // tenantId SAI trong ~1-3s đầu, các hook fetch một lượt (dashboard/todos/unread-chats) fire
        // nhầm tenant → PERMISSION_DENIED hàng loạt (33 dòng /reports 2026-09-14, xem lib/boot-cache.ts).
        // Fix: đối chiếu `bootTenant.id` với claim token HIỆN TẠI (đọc local, KHÔNG force-refresh nên
        // gần như tức thì, không có round-trip mạng) trước khi tin — khớp thì đi đường nhanh như cũ,
        // lệch thì bỏ qua cache (rơi xuống đường chậm bên dưới, tự đọc lại đúng từ claim + tenant doc).
        let cachedTenantConfirmed = true;
        try {
          const token = await firebaseUser.getIdTokenResult(false);
          const claimTid = token.claims.tenantId as string | undefined;
          if (claimTid && claimTid !== bootTenant.id) cachedTenantConfirmed = false;
        } catch (err) {
          logClientError({ route: 'lib/tenant-context.tsx', action: 'confirm_boot_tenant_claim', message: err instanceof Error ? err.message : String(err) });
          // Không xác nhận được → vẫn tin cache như trước (best-effort, không chặn UI vì lỗi phụ).
        }
        if (cachedTenantConfirmed) {
          // 2026-10-18: mở rộng traceUpdate() ngoài 4 hook đã biết — đây là 1 cụm 9 setState NỐI TIẾP
          // chạy NGAY lúc TenantProvider mount (đúng khung cảnh "cơn bão mount sau hard reload" đã nghi
          // từ ca switchWorkspace() nhưng CHƯA từng đo trực tiếp). Xem lib/render-update-tracer.ts.
          // 2026-10-22: crash THẬT sau khi use-todos.ts đã bọc staticTodos (e7713cd7) vẫn tiếp diễn —
          // updateTrace của ca crash kế tiếp (/todos, nanghome) cho thấy CHÍNH cụm này ("bootFast") bắn
          // RAW (không qua queueSharedUpdate) NGAY SÁT building-context:bootCache trong cùng ~400ms —
          // 2 commit Default-lane trần còn lại trong cơn bão mount, cộng dồn cùng các listener ĐÃ bọc
          // (planFeatures/todo-counts/todos-table/unread-chats/staticTodos) vẫn đủ vỡ trần. Bọc nốt cụm
          // này (cùng key `BOOT_KEY`, gộp chung cửa sổ gom với mọi listener khác) để KHÔNG còn commit
          // Default-lane trần nào từ TenantProvider lúc mount.
          traceUpdate('tenant-context:bootFast');
          queueSharedUpdate(BOOT_KEY, () => {
            setTenantId(bootTenant.id);
            setTenantName(bootTenant.name || '');
            setPlan(isPlan(bootTenant.plan) ? bootTenant.plan : DEFAULT_PLAN);
            setBranding((bootTenant.branding as TenantBranding | null) ?? null);
            setTenantLimitOverride((bootTenant.limits as Partial<PlanLimits> | null) ?? {});
            setBillingStatus((bootTenant.billingStatus as BillingStatus | null) ?? 'active');
            setBillingLockAt(bootTenant.billingLockAt ?? null);
            setSuspended(bootTenant.suspended);
            setHolaEnabled(bootTenant.holaEnabled === true);
            setLoading(false);
          });
          return;
        }
        if (cancelled) return;
      }
      setLoading(true);
      setTenantError(null);
      try {
        const token = await firebaseUser.getIdTokenResult();
        const tid = (token.claims.tenantId as string | undefined) || DEFAULT_TENANT_ID;
        if (cancelled) return;
        setTenantId(tid);
        // `withNetworkRetry`: `getDoc` không nhận `AbortSignal` như `fetch` nên không tự thoát khi
        // mạng treo — cùng lớp bug với `lib/building-context.tsx` (xem network-retry.ts). Đường này
        // là FALLBACK (thiếu `bootTenant` hoặc claim lệch) nên hiếm chạy, nhưng chạy thì cũng chặn
        // `TenantLoadingGuard` y hệt nhánh nhanh — không được để treo vô thời hạn.
        const snap = await scheduleBootCall(() => withNetworkRetry(
          () => getDoc(doc(getFirebaseDb(), 'tenants', tid)),
          (attempt, max) => { if (!cancelled) setTenantRetry({ attempt, max }); },
        ));
        if (cancelled) return;
        setTenantRetry(null);
        const data = snap.data();
        traceUpdate('tenant-context:bootSlow');
        setTenantName((data?.name as string | undefined) || '');
        setPlan(isPlan(data?.plan) ? (data.plan) : DEFAULT_PLAN);
        setBranding((data?.branding as TenantBranding | undefined) ?? null);
        setTenantLimitOverride((data?.limits as Partial<PlanLimits> | undefined) ?? {});
        const tenantBilling = data?.billing as TenantBillingMeta | undefined;
        setBillingStatus(tenantBilling?.status ?? 'active');
        setBillingLockAt((tenantBilling?.currentInvoiceFullLockAt as { toMillis?: () => number } | undefined)?.toMillis?.() ?? null);
        setSuspended(data?.status === 'suspended');
        setHolaEnabled(data?.holaEnabled === true);
      } catch (err) {
        if (cancelled) return;
        setTenantRetry(null);
        logClientError({ route: 'lib/tenant-context.tsx', action: 'load_tenant_doc', message: err instanceof Error ? err.message : String(err) });
        // Đã tự thử lại hết số lần cho phép mà vẫn treo/mất kết nối → PHẢI báo, không được lặng lẽ
        // giữ default (default plan/branding trông như tenant thật, user không biết mà thử lại).
        // Lỗi KHÁC (vd permission-denied) giữ hành vi cũ: log rồi dùng default, không chặn màn hình —
        // đường này hiếm khi permission sai vì claim đã qua bước xác nhận ở trên.
        setTenantError('network');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; dropSharedUpdate(BOOT_KEY); };
  }, [firebaseUser?.uid, bootTenant, reloadTick]); // eslint-disable-line react-hooks/exhaustive-deps

  // Định nghĩa gói toàn cục (system/planFeatures) — REALTIME: super-admin sửa → mọi client cập nhật ngay.
  useEffect(() => {
    if (!firebaseUser) return;
    let cancelled = false;
    let unsub: (() => void) | undefined;
    // scheduleBootCall: giãn XUẤT PHÁT so với các lời gọi boot khác dùng chung — xem lib/boot-call-queue.ts.
    void scheduleBootCall(() => onSnapshot(
      doc(getFirebaseDb(), 'system', 'planFeatures'),
      (snap) => {
        traceUpdate('tenant-context:planFeatures');
        const d = snap.data();
        // Gộp với các listener dùng chung khác vào 1 commit transition — xem lib/shared-update-batcher.ts.
        queueSharedUpdate(PLAN_FEATURES_KEY, () => {
          setPlanMatrix(mergePlanMatrix(d?.matrix as Partial<PlanFeatureMatrix> | undefined));
          setPlanLimitsMap(mergePlanLimits(d?.limits as Partial<Record<Plan, Partial<PlanLimits>>> | undefined));
        });
      },
      (err) => logClientError({ route: 'lib/tenant-context.tsx', action: 'listen_plan_features', message: err instanceof Error ? err.message : String(err) }),
    )).then(fn => { if (cancelled) fn(); else unsub = fn; });
    return () => { cancelled = true; unsub?.(); dropSharedUpdate(PLAN_FEATURES_KEY); };
  }, [firebaseUser?.uid]); // eslint-disable-line react-hooks/exhaustive-deps

  const planLimits = useMemo<PlanLimits>(
    () => ({ ...planLimitsMap[plan], ...tenantLimitOverride }),
    [planLimitsMap, plan, tenantLimitOverride],
  );

  // Super-admin (operator gardenia) thấy/truy cập 100% tính năng — bỏ qua gating gói (TRỤC 1).
  // TRỤC 2 (can()) đã tự pass với role admin. Tenant khác vẫn bị giới hạn theo gói.
  const isSuperAdmin = user?.role === 'admin' && tenantId === DEFAULT_TENANT_ID;

  const ctxValue = useMemo<TenantContextType>(
    () => ({
      tenantId, tenantName, plan, branding, loading, planLimits, holaEnabled,
      planAllows: (key) => isSuperAdmin || planMatrixAllows(planMatrix, plan, key),
      planUnlockTier: (key) => PLANS.find(p => planMatrixAllows(planMatrix, p, key)) ?? null,
      billing: { status: isSuperAdmin ? 'exempt' : billingStatus, lockAt: isSuperAdmin ? null : billingLockAt },
      suspended: isSuperAdmin ? false : suspended,
      error: tenantError, retry: tenantRetry, reload,
    }),
    [tenantId, tenantName, plan, branding, loading, planLimits, holaEnabled, isSuperAdmin, planMatrix, billingStatus, billingLockAt, suspended, tenantError, tenantRetry, reload],
  );

  return (
    <TenantContext.Provider value={ctxValue}>
      {children}
    </TenantContext.Provider>
  );
}

export function useTenant() {
  const ctx = useContext(TenantContext);
  if (!ctx) throw new Error('useTenant must be used inside TenantProvider');
  return ctx;
}

/**
 * Helper đọc/ghi Firestore client-side theo tenant hiện tại.
 * Thay thế collection(db,'x') → tcol('x'), doc(db,'x',id) → tdoc('x',id).
 * `raw` là Firestore gốc (để dùng với query/where/orderBy: query(tcol('x'), where(...))).
 */
export function useTenantDb() {
  const { tenantId } = useTenant();
  const db = getFirebaseDb();
  return useMemo(
    () => ({
      tenantId,
      raw: db,
      /** tenants/{tid}/{name} */
      tcol: (name: string) => tenantColClient(db, tenantId, name),
      /** tenants/{tid}/{name}/{id} */
      tdoc: (name: string, id: string) => tenantDocClient(db, tenantId, name, id),
    }),
    [tenantId, db],
  );
}
